/**
 * @module utils/columnSort/fromQB/buildMetricSortItemsFromQB
 * @description 场景 1（未持久化 column_sort）：从 QB 指标区构建列排序中的「指标段」
 *
 * 调用链：{@link buildColumnSortFromQB} 在对照组、对比时间、维度段之后，调用本模块补齐指标列。
 * 本文件只负责「指标 + 列占比虚拟列」，不处理维度、对照组、对比时间。
 *
 * 业务约定（对齐旧 watchPercentageColumnChange / crud addColumnSort）：
 * - column_percentage 配置项匹配键：`table_code::metric`（metric 为物理列名，非展示名）
 * - 勾选列占比的指标，主列后紧跟一条 `%` 前缀虚拟列（column 形如 `%metric_name`）
 * - 维指同名字段时，展示名带聚合前缀，但 pctKey 仍用物理 column 匹配占比配置
 */

import { SortableType } from '@dbl/core/chartEditor';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import {
  buildMetricDisplayInfo,
  getFieldIdentityKey,
  percentColumnForMetric,
} from '../untils/fieldUtils';
import type { IColumnPercentageItem } from '../untils/columnPercentage';
import { normalizeColumnSortItem } from '../untils/keys';

/** 与 column_percentage、syncPercentage 共用：用 table_code + 物理列名定位占比配置 */
function percentageConfigKey(item: IColumnPercentageItem): string {
  return `${item.table_code || 't0'}::${item.metric}`;
}

/** 预建 Set，避免每个指标在 column_percentage 中线性查找 */
function buildPercentageKeySet(
  columnPercentage: IColumnPercentageItem[] | undefined,
): Set<string> {
  return new Set((columnPercentage || []).map(percentageConfigKey));
}

/**
 * 从 QB `columnMetrics` 构建指标段列排序项，并兼顾列占比（column_percentage）。
 *
 * 输出顺序：按 QB 指标区顺序，每个指标一条主列；若该指标在 column_percentage 中勾选，
 * 则紧接其后追加一条 % 虚拟列（与主列共享 table_id / builder_type 等，仅 column/展示名不同）。
 *
 * @param columnMetrics - QB 指标区原始字段（Sortable 拖入的 metric 行）
 * @param dimensionKeySet - 已由 {@link buildDimensionSortItemsFromQB} 构建的维度 identity 集合；
 *   用于判断维指是否重名，从而决定是否给指标展示名加聚合前缀
 * @param columnPercentage - 列占比勾选列表，通常来自 chartSettings draft 或 base
 * @returns 指标段 IColumnSortItem[]；由上层 {@link buildColumnSortFromQB} 与对照组/对比时间/维度段合并
 */
export function buildMetricSortItemsFromQB(
  columnMetrics: unknown[],
  dimensionKeySet: Set<string>,
  columnPercentage?: IColumnPercentageItem[],
): IColumnSortItem[] {
  const pctSet = buildPercentageKeySet(columnPercentage);
  const items: IColumnSortItem[] = [];

  for (const raw of columnMetrics) {
    const row = raw as Record<string, unknown>;

    // 维指 identity 冲突时，展示名需带 aggregate_ 前缀，避免列头与维度列混淆
    const hasDuplicate = dimensionKeySet.has(getFieldIdentityKey(row));
    const metricInfo = buildMetricDisplayInfo(
      {
        column_name: String(row.column_name ?? row.column ?? ''),
        column_display: String(row.column_display ?? ''),
        column_display_en: String(row.column_display_en ?? ''),
        aggregate: row.aggregate as string | undefined,
      },
      hasDuplicate,
    );

    const item = normalizeColumnSortItem({
      table_id: String(row.table_id ?? ''),
      table_code: String(row.table_code ?? 't0'),
      column: metricInfo.column,
      builder_type: SortableType.Metric,
      column_display_cn: metricInfo.column_display_cn,
      column_display_en: metricInfo.column_display_en,
      sql_expression: row.sql_expression as string | undefined,
    });
    items.push(item);

    // 占比匹配用物理 column + table_code，与展示名是否带聚合前缀无关
    const pctKey = `${item.table_code || 't0'}::${item.column}`;
    if (!pctSet.has(pctKey)) {
      continue;
    }

    // 虚拟列继承主指标元数据，仅覆盖 column / 中英文展示名（加 % 前缀）
    const pct = percentColumnForMetric(
      item.column,
      item.column_display_cn,
      item.column_display_en,
    );
    items.push({ ...item, ...pct });
  }

  return items;
}
